// Capacitor 네이티브(Android) 래퍼 전용 - 폰 푸시(FCM) 권한/토큰 등록 + 탭 시 네비게이션.
// index.js에서 initNativeApp()/initNativeDownloads()와 나란히 한 번만 호출한다.
// Capacitor.isNativePlatform()이 false인 일반 웹 브라우저에서는 initNativePush()이 즉시
// 리턴하는 완전한 no-op이다(nativeApp.js와 같은 관례).
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { App as CapacitorApp } from '@capacitor/app';
import client from './api/client';
import { isLoggedIn as isLoggedInPure, safeInternalLink, decideTapAction } from '../utils/pushLink';

const PUSH_CHANNEL_ID = 'kmes_default';
const PENDING_LINK_KEY = 'kmes-push-pending-link';
// 로그인 전에 토큰이 먼저 발급되는 경우(앱 기동 직후)를 위해 마지막 토큰을 로컬에 남겨둔다 -
// 로그인 직후(sendStoredPushTokenIfAny) / 로그아웃 시(deleteStoredPushTokenOnLogout) 재사용.
const STORED_TOKEN_KEY = 'kmes-push-token';

export { safeInternalLink };

// 순수 로직(isLoggedIn/safeInternalLink/decideTapAction)은 ../utils/pushLink.js로 분리했다 -
// 유닛테스트는 거기서 직접 import한다(이 파일은 axios를 끌고 와서 Jest가 못 읽는다).
export function isLoggedIn() {
  return isLoggedInPure(safeStorage());
}

function safeStorage(kind = 'localStorage') {
  if (typeof window === 'undefined') return null;
  try {
    return window[kind];
  } catch (e) {
    return null;
  }
}

// ---- 부수효과 (네이티브에서만 호출됨) ----

function stashPendingLink(link) {
  const storage = safeStorage('sessionStorage');
  try {
    storage?.setItem(PENDING_LINK_KEY, link);
  } catch (e) {
    // 저장 실패해도 치명적이지 않음(그냥 탭한 화면으로 못 가는 정도) - 무시
  }
}

// 로그인 직후 호출 - 탭해둔 링크가 있으면 돌려주고 비운다.
export function consumePendingPushLink() {
  const storage = safeStorage('sessionStorage');
  try {
    const link = storage?.getItem(PENDING_LINK_KEY);
    if (link) storage.removeItem(PENDING_LINK_KEY);
    return link || null;
  } catch (e) {
    return null;
  }
}

function dispatchTapped(link) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('kmes:push-tapped', { detail: { link } }));
}

function dispatchReceived(payload) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('kmes:push-received', { detail: payload }));
}

async function registerTokenWithBackend(token, appVersion) {
  try {
    const deviceInfo = (navigator?.userAgent || '').slice(0, 200);
    await client.post('/api/v1/common/push-token', {
      token,
      platform: 'android',
      deviceInfo,
      appVersion,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[nativePush] 토큰 등록 실패', e);
  }
}

async function handleNewToken(token) {
  const storage = safeStorage();
  try {
    storage?.setItem(STORED_TOKEN_KEY, token);
  } catch (e) {
    // 무시 - 저장 안 돼도 이번 세션 등록 자체는 진행
  }

  let appVersion;
  try {
    const info = await CapacitorApp.getInfo();
    appVersion = info?.version;
  } catch (e) {
    appVersion = undefined;
  }

  // 로그인 전(앱 기동 직후)에 토큰이 먼저 올 수 있다 - 그때는 Authorization 헤더가 없어 401이
  // 난다. 로그인 성공 후 sendStoredPushTokenIfAny()가 STORED_TOKEN_KEY를 보고 다시 보낸다.
  if (isLoggedIn()) {
    await registerTokenWithBackend(token, appVersion);
  }
}

/** "알림 설정" 화면의 "이 기기 푸시 받기" 상태 줄에 쓴다. */
export function isNativePushCapable() {
  return Capacitor.isNativePlatform();
}

/** 이 기기에서 FCM 토큰을 발급받아 로컬에 들고 있는지 - 권한 허용 + 등록 성공 여부의 근사치. */
export function hasRegisteredPushToken() {
  return Boolean(safeStorage()?.getItem(STORED_TOKEN_KEY));
}

/** 로그인 성공 직후(loginSaga) 호출 - 로그인 전에 이미 받아둔 토큰이 있으면 그제서야 보낸다. */
export async function sendStoredPushTokenIfAny() {
  if (!Capacitor.isNativePlatform()) return;
  const storage = safeStorage();
  const token = storage?.getItem(STORED_TOKEN_KEY);
  if (!token) return;

  let appVersion;
  try {
    const info = await CapacitorApp.getInfo();
    appVersion = info?.version;
  } catch (e) {
    appVersion = undefined;
  }
  await registerTokenWithBackend(token, appVersion);
}

/** 로그아웃 시(logoutSaga) 호출 - 이 기기의 토큰을 서버에서 지운다(최선 노력, 실패해도 로그아웃은 진행). */
export async function deleteStoredPushTokenOnLogout() {
  if (!Capacitor.isNativePlatform()) return;
  const storage = safeStorage();
  const token = storage?.getItem(STORED_TOKEN_KEY);
  if (!token) return;
  try {
    await client.delete('/api/v1/common/push-token', { params: { token } });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[nativePush] 토큰 해제 실패(무시하고 로그아웃 진행)', e);
  }
}

export async function initNativePush() {
  if (!Capacitor.isNativePlatform()) return; // 웹: 완전한 no-op

  try {
    // Android 13+(API 33)부터 POST_NOTIFICATIONS 런타임 권한이 필요하다.
    const current = await PushNotifications.checkPermissions();
    let receive = current.receive;
    if (receive !== 'granted') {
      const requested = await PushNotifications.requestPermissions();
      receive = requested.receive;
    }
    if (receive !== 'granted') {
      // 거부해도 앱은 계속 쓸 수 있어야 한다 - 그냥 푸시만 못 받는다.
      return;
    }

    // 알림 채널(중요도 high) - FcmSender가 android.notification.channel_id로 지정하는 값과 짝.
    await PushNotifications.createChannel({
      id: PUSH_CHANNEL_ID,
      name: 'KMES 알림',
      description: '결재, 제조/생산, 채팅 등 KMES 알림',
      importance: 5, // IMPORTANCE_HIGH
      visibility: 1,
    });

    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      handleNewToken(token.value);
    });

    PushNotifications.addListener('registrationError', (err) => {
      // eslint-disable-next-line no-console
      console.warn('[nativePush] 토큰 발급 실패', err);
    });

    // 포그라운드 수신 - React 트리(NativePushBridge)가 스낵바 + 벨 목록 새로고침을 담당한다.
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      dispatchReceived(notification);
    });

    // 탭(백그라운드/종료 상태에서 알림을 눌러 앱을 연 경우 포함)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const link = action?.notification?.data?.link;
      const { action: decision, link: safeLink } = decideTapAction(link, isLoggedIn());
      if (decision === 'stash') {
        stashPendingLink(safeLink);
      } else {
        dispatchTapped(safeLink);
      }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[nativePush] 초기화 실패', e);
  }
}

export default initNativePush;
